/* eslint-disable max-len */
/**
 * CycleActionModals Component
 * 
 * Reusable modal component for invoice actions on billing cycles.
 * Framework-agnostic with configurable callbacks and validation.
 * 
 * @component InvoiceModal
 * @example
 * ```tsx
 * <InvoiceModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   cycle={billingCycle}
 *   onSave={(data) => console.log(data)}
 *   reasonOptions={customReasons}
 * />
 * ```
 * 
 * @performance
 * - Optimized with useCallback for all event handlers
 * - Memoized calculations for totals
 * - Efficient state management
 */

import { useState, useMemo, useCallback, useEffect } from 'react';

import { ExternalLink, CheckCircle2, RotateCcw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/evo/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { Label } from '@/components/evo/ui/label';
import { Textarea } from '@/components/evo/ui/textarea';
import { Button } from '@/components/evo/ui/button';
import { Checkbox } from '@/components/evo/ui/checkbox';
import { CustomDatePicker } from '@/components/evo/ui/custom-date-picker';
import { BillingCycle, SalesOrderPayload } from './types';
import { SalesOrderPayloadEditor } from './SalesOrderPayloadEditor';
import { SalesOrderStatusPills } from './SalesOrderStatusPills';
import { EnvUtils } from '@/utils/env.utils';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { SELECTIVE_BILLING_INVOICE, SELECTIVE_BILLING_REINVOICE } from '@/config/reasonTypes.config';
import { CreateManualSalesOrderByBillingCycle } from '@/graphql/contracts/_generated_/createManualSalesOrderByBillingCycle.mutation';
import { billingBCPath } from '@/utils/path.utlis';
import { browserHistory } from '@/routing/browserHistory';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/evo/ui/alert-dialog';
import useGetPreviewSalesOrderByBillingCycle from
  '@/pages/ContractDetails/RightPanel/Billing/BillingCadence/hooks/useGetPreviewSalesOrderByBillingCycle';
import useCreateManualSalesOrderByBillingCycle from
  '@/pages/ContractDetails/RightPanel/Billing/BillingCadence/hooks/useCreateManualSalesOrderByBillingCycle';
import usePreReinvoicingChecks from
  '@/pages/ContractDetails/RightPanel/Billing/BillingCadence/hooks/useIsReinvoicingAReinvoicedPeriod';


interface InvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cycle?: BillingCycle;
  soByBcId?: string;
  title?: string;
  onSave?: (data: InvoiceSaveData) => void;
  reasonOptions?: Array<{ value: string; label: string; code?: string }>;
  reasonType?: string;
  onDataChanged?: () => void;
}

interface InvoiceSaveData {
  billingCycleId?: string;
  soByBcId?: string;
  newPayloadJson: string;
  reasonCode: string;
  comment: string;
}


const REASON_TYPE_MAP: Record<string, { apiType: string; displayName: string }> = {
  Excluded: { apiType: SELECTIVE_BILLING_INVOICE, displayName: 'Selective Billing Invoice' },
  SoCreated: { apiType: SELECTIVE_BILLING_REINVOICE, displayName: 'Selective Billing Reinvoice' },
};

const DEFAULT_REASON_TYPE_INFO = REASON_TYPE_MAP.SoCreated;

export function InvoiceModal({
  open,
  onOpenChange,
  cycle,
  soByBcId,
  title,
  onSave,
  reasonOptions: propReasonOptions,
  reasonType: propReasonType,
  onDataChanged,
}: InvoiceModalProps) {
  // Hooks for API calls
  const { reasonCodes, getReasonCodes } = useGetReasonCodes();
  const {
    getPreviewSalesOrderByBillingCycle, isLoadingPreviewSalesOrder, previewSalesOrder,
  } = useGetPreviewSalesOrderByBillingCycle();
  const {
    runPreReinvoicingChecks, isLoadingPreReinvoicingChecks,
  } = usePreReinvoicingChecks();
  const {
    createManualSalesOrderByBillingCycle, isLoadingCreateManualSalesOrder,
  } = useCreateManualSalesOrderByBillingCycle({
    onSuccess: onDataChanged,
  });

  // State: Form fields
  const [reasonName, setReasonName] = useState('');
  const [comment, setComment] = useState('');
  const [currentPayload, setCurrentPayload] = useState<SalesOrderPayload | null>(null);
  const [originalPayload, setOriginalPayload] = useState<SalesOrderPayload | null>(null);
  const [isPayloadValid, setIsPayloadValid] = useState(true);
  const [, setValidationErrors] = useState<string[]>([]);
  const [instantSend, setInstantSend] = useState(false);

  // Default to today's date
  const getTodayString = () => new Date().toISOString().split('T')[0];
  const [invoiceDate, setInvoiceDate] = useState<string>(getTodayString());
  const [isInvoiceDateValid, setIsInvoiceDateValid] = useState(true);

  // State: Bulk billing dates
  const [bulkBillingStartDate, setBulkBillingStartDate] = useState<string>('');
  const [bulkBillingEndDate, setBulkBillingEndDate] = useState<string>('');

  // State: Created sales order (for success view)
  type CreatedSalesOrder = CreateManualSalesOrderByBillingCycle['createManualSalesOrderByBillingCycle'];
  const [createdSalesOrder, setCreatedSalesOrder] = useState<CreatedSalesOrder>(null);

  // State: Pre-reinvoicing warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningFlags, setWarningFlags] = useState<{
    reinvoice: boolean;
    activePending: boolean;
    reinvoicedPeriods: Array<{ billingStartDate: string; billingEndDate?: string | null }>;
    activePendingPeriods: Array<{ billingStartDate: string; billingEndDate?: string | null }>;
  }>({ reinvoice: false, activePending: false, reinvoicedPeriods: [], activePendingPeriods: [] });
  const [pendingSaveData, setPendingSaveData] = useState<InvoiceSaveData | null>(null);

  // State: Instant send confirmation dialog
  const [showInstantSendWarning, setShowInstantSendWarning] = useState(false);

  // Determine reason type based on cycle processing status
  const reasonTypeInfo = cycle?.processingStatus
    ? REASON_TYPE_MAP[cycle.processingStatus] ?? DEFAULT_REASON_TYPE_INFO
    : DEFAULT_REASON_TYPE_INFO;

  const reasonType = propReasonType ?? reasonTypeInfo.displayName;

  // Fetch reason codes based on cycle processing status
  useEffect(() => {
    getReasonCodes(reasonTypeInfo.apiType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonTypeInfo.apiType]);

  // Fetch preview sales order when modal opens
  useEffect(() => {
    if (open) {
      if (soByBcId) {
        getPreviewSalesOrderByBillingCycle({
          soByBcId,
        });
      } else if (cycle?.firstSalesOrderId) {
        getPreviewSalesOrderByBillingCycle({
          soByBcId: cycle.firstSalesOrderId,
        });
      } else if (cycle?.billingCycleID) {
        getPreviewSalesOrderByBillingCycle({
          billingCycleId: cycle.billingCycleID,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cycle?.billingCycleID, cycle?.firstSalesOrderId, soByBcId]);

  // Reset modal state when modal closes (Issue 1 fix)
  useEffect(() => {
    if (!open) {
      setCreatedSalesOrder(null);
      setReasonName('');
      setComment('');
      setCurrentPayload(null);
      setOriginalPayload(null);
      setIsPayloadValid(true);
      setValidationErrors([]);
      // Reset invoice date to today (Feature 1)
      setInvoiceDate(getTodayString());
      setIsInvoiceDateValid(true);
      setInstantSend(false);
      // Reset bulk billing dates
      setBulkBillingStartDate('');
      setBulkBillingEndDate('');
    }
  }, [open]);

  // Memoized reason options from API
  const reasonOptions = useMemo(() => {
    if (propReasonOptions) {
      return propReasonOptions;
    }
    return reasonCodes.map((rc) => ({
      value: rc.code,
      label: rc.name,
      code: rc.code,
    }));
  }, [propReasonOptions, reasonCodes]);

  // Parse the payload from the API response
  const parsedInitialPayload = useMemo(() => {
    if (previewSalesOrder?.payload) {
      try {
        return JSON.parse(previewSalesOrder.payload) as SalesOrderPayload;
      } catch (error) {
        console.error('Error parsing sales order payload:', error);
        return null;
      }
    }
    return null;
  }, [previewSalesOrder?.payload]);

  // Check if bulk billing dates are valid (end date must not be before start date)
  const areBulkDatesValid = useMemo(() => {
    if (bulkBillingStartDate && bulkBillingEndDate) {
      return bulkBillingStartDate <= bulkBillingEndDate;
    }
    return true;
  }, [bulkBillingStartDate, bulkBillingEndDate]);

  // Validate excluded cycle date coverage for BULK dates only.
  // Line-level date coverage is validated separately in JsonTreeEditorV4.
  const excludedCycleStartDateError = useMemo<string | null>(() => {
    if (cycle?.processingStatus !== 'Excluded') return null;
    if (!cycle.startDate || !cycle.endDate) return null;
    if (!bulkBillingStartDate) return null;
    const cycleStart = cycle.startDate.split('T')[0];

    if (bulkBillingStartDate > cycleStart) {
      return `Billing start date cannot be after the original start date (${cycleStart})`;
    }
    return null;
  }, [cycle?.processingStatus, cycle?.startDate, cycle?.endDate, bulkBillingStartDate]);

  const excludedCycleEndDateError = useMemo<string | null>(() => {
    if (cycle?.processingStatus !== 'Excluded') return null;
    if (!cycle.startDate || !cycle.endDate) return null;
    if (!bulkBillingEndDate) return null;
    const cycleEnd = cycle.endDate.split('T')[0];

    if (bulkBillingEndDate < cycleEnd) {
      return `Billing end date cannot be before the original end date (${cycleEnd})`;
    }
    return null;
  }, [cycle?.processingStatus, cycle?.startDate, cycle?.endDate, bulkBillingEndDate]);

  const hasExcludedCycleDateError = excludedCycleStartDateError !== null || excludedCycleEndDateError !== null;

  // Check if Save button should be disabled - reason code, comment required, payload and invoice date must be valid
  const isSaveDisabled = useMemo(() => {
    return !reasonName || !comment.trim() || isLoadingCreateManualSalesOrder || !isPayloadValid || !invoiceDate || !isInvoiceDateValid || !areBulkDatesValid || hasExcludedCycleDateError;
  }, [reasonName, comment, isLoadingCreateManualSalesOrder, isPayloadValid, invoiceDate, isInvoiceDateValid, areBulkDatesValid, hasExcludedCycleDateError]);

  const isCheckingOverlaps = isLoadingPreReinvoicingChecks;

  // Memoized: Modal title based on cycle status or custom title
  const modalTitle = useMemo(() => {
    if (title) return title;
    return cycle?.processingStatus === 'SoCreated' ? 'Reinvoice Cycle' : 'Invoice Cycle';
  }, [cycle?.processingStatus, title]);

  // Memoized: Reason name to code mapping
  const reasonNameToCode = useMemo(() => {
    const mapping: Record<string, string> = {};
    reasonOptions.forEach((option) => {
      if (option.code) {
        mapping[option.value] = option.code;
      }
    });
    return mapping;
  }, [reasonOptions]);

  // Memoized: Calculate totals - UnitCost already reflects the correct 3-tier value (Original / ProRata / User)
  const calculateTotal = useCallback((payload: SalesOrderPayload | null): number => {
    if (!payload || !payload.ContractLines) return 0;
    return payload.ContractLines.reduce((sum, line) => {
      return sum + ((line.Quantity ?? 0) * (line.UnitCost ?? 0));
    }, 0);
  }, []);

  const originalTotal = useMemo(() => calculateTotal(originalPayload), [originalPayload, calculateTotal]);
  const currentTotal = useMemo(() => calculateTotal(currentPayload), [currentPayload, calculateTotal]);
  const currencyCode = (currentPayload?.CurrencyIsoCode as string) || (originalPayload?.CurrencyIsoCode as string) || 'USD';

  // Utility: Format currency
  const formatCurrency = useCallback((amount: number, currency: string): string => {
    return `${currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  // Optimized handlers with useCallback
  const handleReasonNameChange = useCallback((value: string) => {
    setReasonName(value);
  }, []);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  }, []);

  const handlePayloadSave = useCallback((payload: SalesOrderPayload) => {
    setCurrentPayload(payload);
  }, []);

  const handleOriginalPayloadLoad = useCallback((payload: SalesOrderPayload) => {
    setOriginalPayload(payload);
  }, []);

  const handleValidationChange = useCallback((isValid: boolean, errors: string[]) => {
    setIsPayloadValid(isValid);
    setValidationErrors(errors);
  }, []);

  // Handler for invoice date change (Feature 1)
  const handleInvoiceDateChange = useCallback((date: string) => {
    setInvoiceDate(date);
  }, []);

  // Handler for invoice date validation change (Feature 1)
  const handleInvoiceDateValidationChange = useCallback((valid: boolean) => {
    setIsInvoiceDateValid(valid);
  }, []);

  // Handlers for bulk billing dates
  const handleBulkBillingStartDateChange = useCallback((date: string) => {
    setBulkBillingStartDate(date);
  }, []);

  const handleBulkBillingEndDateChange = useCallback((date: string) => {
    setBulkBillingEndDate(date);
  }, []);

  const handleBulkBillingStartDateReset = useCallback(() => {
    setBulkBillingStartDate('');
  }, []);

  const handleBulkBillingEndDateReset = useCallback(() => {
    setBulkBillingEndDate('');
  }, []);

  // Format date as dd/mm/yyyy
  const formatDate = useCallback((dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return 'N/A';
    }
  }, []);

  // Handle redirect to Sales Orders grid filtered by salesOrderId
  const handleGoToSalesOrder = useCallback(() => {
    if (!createdSalesOrder?.salesOrderID) {
      return;
    }

    const basePath = `/${billingBCPath}`;
    const link = `${basePath}?salesOrderId=${createdSalesOrder.salesOrderID}`;
    const targetUrl = getParameterizedLink(link);

    // Close the modal before navigating
    onOpenChange(false);

    // Use SPA navigation - the Sales Orders grid will remount via key prop when query params change
    browserHistory.push(targetUrl);
  }, [createdSalesOrder?.salesOrderID]);

  const executeCreateManualSalesOrder = useCallback(async (saveData: InvoiceSaveData) => {
    const invoiceDateISO = invoiceDate ? `${invoiceDate}T00:00:00.000Z` : undefined;

    try {
      const response = await createManualSalesOrderByBillingCycle({
        soByBcId: saveData.soByBcId,
        billingCycleId: saveData.billingCycleId,
        newPayloadJson: saveData.newPayloadJson,
        reasonCode: saveData.reasonCode,
        comment: saveData.comment,
        invoiceDate: invoiceDateISO,
        instantSend,
      } as Parameters<typeof createManualSalesOrderByBillingCycle>[0]);

      if (onSave) {
        onSave(saveData);
      }

      if (response) {
        setCreatedSalesOrder(response);
      }
    } catch (error) {
      console.error('Error creating manual sales order:', error);
    }
  }, [createManualSalesOrderByBillingCycle, onSave, invoiceDate, instantSend]);

  const handleWarningConfirm = useCallback(async () => {
    setShowWarningDialog(false);
    setWarningFlags({ reinvoice: false, activePending: false, reinvoicedPeriods: [], activePendingPeriods: [] });
    if (pendingSaveData) {
      await executeCreateManualSalesOrder(pendingSaveData);
      setPendingSaveData(null);
    }
  }, [pendingSaveData, executeCreateManualSalesOrder]);

  const handleWarningCancel = useCallback(() => {
    setShowWarningDialog(false);
    setWarningFlags({ reinvoice: false, activePending: false, reinvoicedPeriods: [], activePendingPeriods: [] });
    setPendingSaveData(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!reasonName || !comment.trim()) {
      return;
    }

    if (!currentPayload) {
      alert('Payload data is not available.');
      return;
    }

    const effectiveSoByBcId = soByBcId || cycle?.firstSalesOrderId || undefined;
    const outputData: InvoiceSaveData = {
      soByBcId: effectiveSoByBcId,
      billingCycleId: effectiveSoByBcId ? undefined : cycle?.billingCycleID,
      newPayloadJson: JSON.stringify(currentPayload),
      reasonCode: reasonNameToCode[reasonName] || reasonName,
      comment: comment.trim(),
    };

    const parsedPayload: SalesOrderPayload = JSON.parse(outputData.newPayloadJson);
    const contractLines = parsedPayload.ContractLines ?? [];
    const items = contractLines.map((line) => ({
      contractId: String(line.ContractID ?? line.ContractLineID ?? ''),
      contractLineId: String(line.ContractLineID ?? ''),
      billingStartDate: String(line.BillingStartDate ?? ''),
      billingEndDate: line.BillingEndDate ? String(line.BillingEndDate) : undefined,
    }));

    if (items.length > 0) {
      const checks = await runPreReinvoicingChecks({ items });

      const hasReinvoice = checks?.isReinvoicingAlreadyReinvoicedPeriod ?? false;
      const hasActivePending = checks?.isReinvoicingActivePendingPeriods ?? false;

      if (hasReinvoice || hasActivePending) {
        setPendingSaveData(outputData);
        setWarningFlags({
          reinvoice: hasReinvoice,
          activePending: hasActivePending,
          reinvoicedPeriods: (checks?.reinvoicedPeriods ?? []).filter((p): p is NonNullable<typeof p> => p !== null),
          activePendingPeriods: (checks?.activePendingPeriods ?? []).filter((p): p is NonNullable<typeof p> => p !== null),
        });
        setShowWarningDialog(true);
        return;
      }
    }

    await executeCreateManualSalesOrder(outputData);
  }, [
    reasonName, comment, currentPayload, cycle?.billingCycleID, cycle?.firstSalesOrderId, soByBcId, reasonNameToCode,
    runPreReinvoicingChecks, executeCreateManualSalesOrder,
  ]);

  // Success view after sales order is created
  if (createdSalesOrder) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-xl flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              Sales Order Created!
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">ID:</span>
                <span className="text-sm font-semibold">{createdSalesOrder.salesOrderID}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Invoice Date:</span>
                <span className="text-sm font-semibold">{formatDate(createdSalesOrder.sendingDate)}</span>
              </div>
            </div>

            <div className="pt-2">
              <SalesOrderStatusPills
                source={createdSalesOrder.source}
                status={createdSalesOrder.salesOrderStatus}
              />
            </div>

            <div className="pt-4 flex justify-center">
              <Button onClick={handleGoToSalesOrder} className="gap-2">
                Go to Sales Order
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] flex flex-col">
          <DialogHeader className="pb-1 flex-shrink-0">
            <DialogTitle className="text-lg">{modalTitle}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0 gap-2 pb-2">
            <div className="p-1.5 bg-muted/30 rounded-lg border flex-shrink-0">
              <div className="grid grid-cols-[3fr_1fr] gap-2">
                <div className="flex flex-col gap-1.5">
                  <div className="grid grid-cols-3 gap-1.5">
                    <div>
                      <Label htmlFor="invoice-reason-type" className="text-[11px]">
                        Reason Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={reasonType} disabled>
                        <SelectTrigger id="invoice-reason-type" className="h-7 text-xs">
                          <SelectValue placeholder="Select reason type">{reasonType}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={reasonType}>{reasonType}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="invoice-reason-name" className="text-[11px]">
                        Reason Name <span className="text-destructive">*</span>
                      </Label>
                      <Select value={reasonName} onValueChange={handleReasonNameChange}>
                        <SelectTrigger id="invoice-reason-name" className="h-7 text-xs">
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {reasonOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="invoice-date" className="text-[11px]">
                        Invoice Date <span className="text-destructive">*</span>
                      </Label>
                      <CustomDatePicker
                        value={invoiceDate}
                        onChange={handleInvoiceDateChange}
                        onValidationChange={handleInvoiceDateValidationChange}
                        placeholder="Pick a date"
                        allowManualInput={true}
                        required={true}
                        showValidationError={true}
                        minDate={getTodayString()}
                        buttonClassName="h-7"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5">
                    <div>
                      <Label className="text-[11px]">Bulk Start Date</Label>
                      <div className="flex items-center gap-1">
                        <CustomDatePicker
                          value={bulkBillingStartDate}
                          onChange={handleBulkBillingStartDateChange}
                          placeholder="Pick a date"
                          allowManualInput={true}
                          buttonClassName="h-7"
                          maxDate={bulkBillingEndDate || undefined}
                          showValidationError={true}
                          errorMessage={bulkBillingStartDate && bulkBillingEndDate && bulkBillingStartDate > bulkBillingEndDate ? 'Cannot be after End Date' : undefined}
                        />
                        {bulkBillingStartDate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 hover:bg-gray-100"
                            onClick={handleBulkBillingStartDateReset}
                          >
                            <RotateCcw className="h-3 w-3 text-blue-600" />
                          </Button>
                        )}
                      </div>
                      {excludedCycleStartDateError && (
                        <p className="text-[10px] text-destructive mt-0.5">{excludedCycleStartDateError}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-[11px]">Bulk End Date</Label>
                      <div className="flex items-center gap-1">
                        <CustomDatePicker
                          value={bulkBillingEndDate}
                          onChange={handleBulkBillingEndDateChange}
                          placeholder="Pick a date"
                          allowManualInput={true}
                          buttonClassName="h-7"
                          minDate={bulkBillingStartDate || undefined}
                          showValidationError={true}
                          errorMessage={bulkBillingStartDate && bulkBillingEndDate && bulkBillingEndDate < bulkBillingStartDate ? 'Cannot be before Start Date' : undefined}
                        />
                        {bulkBillingEndDate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 hover:bg-gray-100"
                            onClick={handleBulkBillingEndDateReset}
                          >
                            <RotateCcw className="h-3 w-3 text-blue-600" />
                          </Button>
                        )}
                      </div>
                      {excludedCycleEndDateError && (
                        <p className="text-[10px] text-destructive mt-0.5">{excludedCycleEndDateError}</p>
                      )}
                    </div>

                    {EnvUtils.isInstantSendEnabled() && (
                      <div className="flex flex-col items-start justify-center">
                        <Label htmlFor="instant-send" className="text-[11px] cursor-pointer">
                          Instant Send
                        </Label>
                        <Checkbox
                          id="instant-send"
                          checked={instantSend}
                          className="h-6 w-6"
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setShowInstantSendWarning(true);
                            } else {
                              setInstantSend(false);
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col">
                  <Label htmlFor="invoice-comment" className="text-[11px]">
                    Comment <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="invoice-comment"
                    value={comment}
                    onChange={handleCommentChange}
                    placeholder="Include Salesforce ref or CN ref & short comment to explain your action"
                    className="resize-none flex-1 text-xs"
                  />
                  <div className="flex justify-end mt-1">
                    <Button
                      onClick={handleSave}
                      size="sm"
                      className="h-7"
                      disabled={isSaveDisabled || isCheckingOverlaps}
                    >
                      {isCheckingOverlaps
                        ? 'Checking...' : isLoadingCreateManualSalesOrder
                          ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <SalesOrderPayloadEditor
                onSave={handlePayloadSave}
                onOriginalPayloadLoad={handleOriginalPayloadLoad}
                currentBillingCycleContractLineId={cycle?.contractLineID}
                cycle={cycle}
                salesOrderStatus={previewSalesOrder?.salesOrderStatus}
                salesOrderSource={previewSalesOrder?.source}
                initialPayload={parsedInitialPayload}
                isLoading={isLoadingPreviewSalesOrder}
                shouldShowOriginalPayloadBadges={cycle?.processingStatus !== 'Excluded'}
                onValidationChange={handleValidationChange}
                bulkBillingStartDate={bulkBillingStartDate}
                bulkBillingEndDate={bulkBillingEndDate}
                formattedOriginalTotal={formatCurrency(originalTotal, currencyCode)}
                formattedCurrentTotal={formatCurrency(currentTotal, currencyCode)}
                totalsChanged={currentTotal !== originalTotal}
                currentTotalHigher={currentTotal > originalTotal}
                excludedCycleStartDate={cycle?.processingStatus === 'Excluded' ? cycle.startDate?.split('T')[0] : undefined}
                excludedCycleEndDate={cycle?.processingStatus === 'Excluded' ? cycle.endDate?.split('T')[0] : undefined}
              />
            </div>
          </div>

        </DialogContent>
      </Dialog>

      <AlertDialog open={showInstantSendWarning} onOpenChange={setShowInstantSendWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Instant Send Confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              You have selected instant send, this will send the SO to Dimensions instantly. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowInstantSendWarning(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setInstantSend(true);
              setShowInstantSendWarning(false);
            }}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pre-Reinvoicing Warning</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {warningFlags.reinvoice && (
                  <div>
                    <p>This period has already been manually reinvoiced. Are you sure you want to reinvoice it?</p>
                    {warningFlags.reinvoicedPeriods.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium">Already reinvoiced periods:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                          {warningFlags.reinvoicedPeriods.map((period, idx) => (
                            <li key={idx} className="text-sm">
                              {formatDate(period.billingStartDate)} – {formatDate(period.billingEndDate)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {warningFlags.activePending && (
                  <div>
                    <p>Your invoice overlaps with a period C&amp;B will bill for. </p>

                    {warningFlags.activePendingPeriods.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium">Overlapping active pending periods:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                          {warningFlags.activePendingPeriods.map((period, idx) => (
                            <li key={idx} className="text-sm">
                              {formatDate(period.billingStartDate)} – {formatDate(period.billingEndDate)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <p> To avoid the customer being charged twice, please either:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Adjust your billing cycle start date(s)</li>
                    <li>Cancel to stop this process</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleWarningCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWarningConfirm}>Continue Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


